import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router-dom'

import { Box, Skeleton, Typography } from '@mui/material'

import GRTabNavigation from '../../../components/GRTabNavigation/GRTabNavigation'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { useGetInternalOrganizationsQuery } from '../../api/organizations'
import { useGetInternalOrganizationPermissionsQuery } from '../../api/permissions'
import { BackButton } from '../../components/BackButton/BackButton'
import { OrganizationLiveEventGamesAccess } from '../../features/organizations/components/OrganizationLiveEventGamesAccess/OrganizationLiveEventGamesAccess'
import { OrganizationReportsAccess } from '../../features/organizations/components/OrganizationReportsAccess/OrganizationReportsAccess'
import { AccessTab, accessTabLabelKeys, accessTabs } from '../../features/organizations/components/helpers/organizationAccessTabHelpers'

export const OrganizationAccessPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-organization-access'))
  const { t } = useTranslation()
  const { organizationId = '', tab } = useParams<{ organizationId: string; tab?: string }>()
  const organizationsQuery = useGetInternalOrganizationsQuery()
  // Other admins and the legacy app change the same access.
  const permissionsQuery = useGetInternalOrganizationPermissionsQuery(organizationId, { refetchOnMountOrArgChange: true })
  const selectedIndex = accessTabs.indexOf(tab as AccessTab)

  if (selectedIndex < 0) return <Navigate to={`/internal/organizations/${organizationId}/access/${accessTabs[0]}`} replace />

  const stored = organizationsQuery.data?.find(({ id }) => id === organizationId)
  const organization = stored ? { id: stored.id, name: stored.name || stored.id } : undefined
  const permissions = permissionsQuery.data

  const content = () => {
    if (organizationsQuery.isLoading || permissionsQuery.isLoading) return <Skeleton variant="rectangular" height={480} />
    if (organizationsQuery.isError) return <Typography color="error.main">{t('internal-csm:organizations_load_failed')}</Typography>
    if (!organization) return <Typography color="error.main">{t('internal-csm:organization_access_organization_not_found')}</Typography>
    if (!permissions) return <Typography color="error.main">{t('internal-csm:organization_access_load_failed')}</Typography>
    return accessTabs[selectedIndex] === 'reports' ? (
      <OrganizationReportsAccess organization={organization} permissions={permissions} />
    ) : (
      <OrganizationLiveEventGamesAccess organization={organization} permissions={permissions} />
    )
  }

  return (
    <Box>
      <BackButton url="/internal/organizations" text={t('internal-csm:organization_access_back')} />
      <Typography variant="h5" fontWeight="bold" mb={2} sx={{ overflowWrap: 'anywhere' }}>
        {organization ? t('internal-csm:organization_access_heading', { name: organization.name }) : t('internal-sidebar:organization_access')}
      </Typography>
      <GRTabNavigation
        tabs={accessTabs.map((id) => ({ id, name: t(accessTabLabelKeys[id]) }))}
        selectedTab={selectedIndex}
        pagePrefix={`/internal/organizations/${organizationId}/access`}
        sticky={false}
      />
      {content()}
    </Box>
  )
}
